/**
 * The OPR Pricing tab for an HRT / TRT patient. It prices the products ticked on the HRT / TRT Plan tab from the
 * same price book (`useOprPrices`, one query key), so a price set here is on the plan tab at once.
 *
 * When every ticked product is linked to a therapy library treatment, this is the usual OPR Pricing tab
 * (OprPricingTab.tsx) for those treatments. A hormone product that is not linked yet cannot be shown there, so the
 * tab lists the ticked products with the same price box and the same dark banner instead. Costs, margins and OPR
 * stay on clinic screens only (PG-006).
 */

import { useState } from 'react';
import { useOprPrices, PriceBox, PricingBanner, dollars, toCents, type OprItem } from './OprPricing';
import { OprPricingTab } from './OprPricingTab';
import { HrtChip, SaveNote } from './HrtUi';
import { initialPick, type HrtPick } from './HrtPlanTab';
import { libraryDoseLine, pathwaySchedule, type HrtPatientView } from '../lib/hrt';

export function HrtOprPricing({
  view,
  pick,
  firstName,
  lastName,
  onOpenPlan,
}: {
  view: HrtPatientView;
  pick: HrtPick | null;
  firstName: string;
  lastName: string;
  onOpenPlan: () => void;
}) {
  const prices = useOprPrices();
  const current = pick ?? initialPick(view);
  const pathway = view.evaluation?.pathways.find((p) => p.id === current.pathwayId) ?? null;
  const products = (pathway ? view.products[pathway.id] ?? [] : []).filter((p) => current.products.includes(p.medicationId));
  const items = products.map((p) => (p.formularyItemId ? prices.byItemId.get(p.formularyItemId) ?? null : null));
  const treatmentIds = items.map((item) => item?.treatmentId ?? null);

  if (prices.isLoading) {
    return <section className="rounded-[10px] border border-hairline bg-white px-5 py-8 text-[14px] text-ink-muted">Loading prices...</section>;
  }
  if (prices.error) {
    return <section className="rounded-[10px] border border-hairline bg-white px-5 py-8 text-[14px] text-alert">Could not load the price book.</section>;
  }
  if (products.length > 0 && treatmentIds.every(Boolean)) {
    return <OprPricingTab patientId={view.patient.id} firstName={firstName} lastName={lastName} selected={treatmentIds as string[]} />;
  }
  if (!pathway || products.length === 0) {
    return (
      <section className="rounded-[10px] border border-dashed border-hairline bg-white px-5 py-10 text-center">
        <p className="font-display text-[18px] text-navy">No products to price yet</p>
        <p className="mx-auto mt-1 max-w-md text-[13.5px] text-ink-muted">Pick a pathway and tick its products on the HRT / TRT Plan tab, and their prices appear here.</p>
        <button type="button" onClick={onOpenPlan} className="mt-3 text-[13.5px] font-semibold text-forest underline underline-offset-2">
          Open the HRT / TRT Plan
        </button>
      </section>
    );
  }

  const priced = items.filter((item): item is OprItem => Boolean(item));
  return (
    <section className="flex flex-col gap-4 rounded-[14px] border border-hairline bg-white p-4 sm:p-6" data-testid="hrt-opr-tab">
      <div>
        <h2 className="font-display text-[28px] font-semibold leading-tight text-navy">OPR Pricing</h2>
        <p className="mt-1.5 text-[15px] text-ink-muted">
          {pathway.name}. Same prices as the HRT / TRT Plan tab; change them here or there and both stay in sync.
        </p>
      </div>
      <ul className="divide-y divide-hairline rounded-[12px] border border-hairline">
        {products.map((product, index) => {
          const item = items[index] ?? null;
          const schedule = product.controlledSchedule ? pathwaySchedule([product]) : null;
          return (
            <li key={product.medicationId} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3.5" data-testid={`hrt-opr-${product.medicationId}`}>
              <div className="min-w-0 flex-1 basis-[220px]">
                <p className="text-[15px] font-semibold text-navy">
                  {product.name}
                  {product.role === 'adjunct' ? ' (add-on)' : ''}
                </p>
                {schedule && (
                  <p className="mt-1">
                    <HrtChip>Controlled: {schedule}</HrtChip>
                  </p>
                )}
                <p className="mt-1 text-[13px] text-ink-muted">{item?.doseLine ?? libraryDoseLine(product)}</p>
                {product.heldBack && <p className="mt-0.5 text-[12.5px] font-medium text-risk-yellow">Not on a plan until MCOS approves the exact product.</p>}
                {item && <p className="mt-0.5 text-[12.5px] text-ink-muted">{item.opr.basisText}</p>}
              </div>
              {item ? (
                <div className="flex flex-wrap items-start gap-3">
                  <CostBox item={item} prices={prices} />
                  <PriceBox item={item} costHint={false} onSave={(change) => prices.save(item.itemId, change)} onDraft={(cents) => prices.setDraft(item.itemId, cents)} />
                </div>
              ) : (
                <span className="rounded-[9px] border border-dashed border-hairline px-3 py-2.5 text-[12.5px] text-ink-muted">Not in your price book</span>
              )}
            </li>
          );
        })}
      </ul>
      <PricingBanner items={priced} unpriced={products.length - priced.length} firstName={firstName} />
      {prices.saveError && <SaveNote tone="error">{prices.saveError}</SaveNote>}
    </section>
  );
}

/** The clinic's own pharmacy cost for one product: the margin in the banner reads it. Clinic only (PG-006). */
function CostBox({ item, prices }: { item: OprItem; prices: ReturnType<typeof useOprPrices> }) {
  const [text, setText] = useState<string | null>(null);
  const own = item.clinic.ownCostCents;
  const commit = () => {
    if (text === null) return;
    const typed = text.trim() === '' ? null : toCents(text);
    setText(null);
    if (typed !== own) prices.save(item.itemId, { costCents: typed });
  };
  return (
    <div className="w-[132px]">
      <label className="flex h-11 items-center gap-[3px] rounded-[9px] border border-hairline bg-white px-2.5">
        <span className="text-[15px] text-ink-muted">$</span>
        <input
          inputMode="decimal"
          aria-label={`Pharmacy cost for ${item.name}`}
          className="w-[60px] min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] font-medium text-navy outline-none placeholder:text-ink-muted"
          placeholder="cost"
          value={text ?? dollars(own)}
          onFocus={() => setText(dollars(own))}
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => event.key === 'Enter' && (event.target as HTMLInputElement).blur()}
        />
        <span className="text-[12px] text-ink-muted">{item.unitLabel}</span>
      </label>
      <div className="mt-1 text-[12px] text-ink-muted">{own != null ? 'Your cost' : 'Your pharmacy cost'}</div>
    </div>
  );
}
